import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiClipboard, FiUser, FiCheck, FiAlertTriangle, FiRefreshCw, FiClock } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { useRoutinesStats } from '../../context/RoutinesStatsContext';
import { getRecentRoutineRuns, type RecentRoutineRun } from '../../services/momentumRoutinesService';
import '../../styles/CommandCenterSidebar.css';

const quickActions = [
  { label: 'New Routine',   icon: <FiPlus size={13} />,      color: '#a200ff', iconBg: 'rgba(162,0,255,0.14)',  route: ROUTES.MOMENTUM_ROUTINES + '?new=1' },
  // Library hidden; add { label: 'Library', icon: <FiBookmark size={13} />, color: '#00b0d3', iconBg: 'rgba(0,176,211,0.14)', route: ROUTES.MOMENTUM_ROUTINES + '?library=1' } back to show it.
  { label: 'Command Center',icon: <FiClipboard size={13} />, color: '#6366f1', iconBg: 'rgba(99,102,241,0.14)', route: ROUTES.MOMENTUM_COMMAND },
  { label: 'Agents',        icon: <FiUser size={13} />,      color: '#f59e0b', iconBg: 'rgba(245,158,11,0.14)', route: ROUTES.DIGITAL_WORKFORCE },
];

const RUN_STATUS_CFG: Record<string, { label: string; color: string; bg: string; Icon: React.ElementType }> = {
  done:    { label: 'Done',    color: '#34d399', bg: 'rgba(16,185,129,0.14)', Icon: FiCheck },
  failed:  { label: 'Failed',  color: '#f87171', bg: 'rgba(239,68,68,0.12)', Icon: FiAlertTriangle },
  running: { label: 'Running', color: '#fbbf24', bg: 'rgba(245,158,11,0.14)', Icon: FiRefreshCw },
};

function formatRunTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0 || Number.isNaN(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatRunDuration(sec?: number): string {
  if (sec == null || sec < 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? ` · ${m}m ${s}s` : ` · ${s}s`;
}

const RoutinesSidebarContent: React.FC = () => {
  const navigate = useNavigate();
  const { stats } = useRoutinesStats();
  const [recentRuns, setRecentRuns] = useState<RecentRoutineRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);

  // Fetch once on mount — no polling.
  useEffect(() => {
    getRecentRoutineRuns(10)
      .then(setRecentRuns)
      .catch(() => setRecentRuns([]))
      .finally(() => setRunsLoading(false));
  }, []);

  return (
    <div className="cc-sb-dashboard">
      <div className="cc-sb-stats">
        {[
          { cls: 'cc-sb-stat--purple', lbl: 'TOTAL ROUTINES', ctx: 'All scheduled routines', val: stats.total,  numCls: 'cc-sb-stat__num--purple' },
          { cls: 'cc-sb-stat--green',  lbl: 'ACTIVE',          ctx: 'Running on schedule',    val: stats.active, numCls: 'cc-sb-stat__num--green'  },
          { cls: 'cc-sb-stat--amber',  lbl: 'PAUSED',          ctx: 'Temporarily disabled',   val: stats.paused, numCls: 'cc-sb-stat__num--amber'  },
        ].map((s, i) => (
          <div key={i} className={`cc-sb-stat ${s.cls}`}>
            <div className="cc-sb-stat__body">
              <span className="cc-sb-stat__label">{s.lbl}</span>
              <span className="cc-sb-stat__context">{s.ctx}</span>
            </div>
            <span className={`cc-sb-stat__num ${s.numCls}`}>{s.val}</span>
          </div>
        ))}
      </div>

      <div className="cc-sb-quick-actions">
        <div className="cc-sb-section-lbl">Quick Actions</div>
        <div className="cc-sb-qa-grid">
          {quickActions.map((a, i) => (
            <button key={i} className="cc-sb-qa-btn" onClick={() => navigate(a.route)}>
              <div className="cc-sb-qa-icon" style={{ background: a.iconBg, color: a.color }}>{a.icon}</div>
              <span className="cc-sb-qa-label">{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="cc-sb-quick-actions">
        <div className="cc-sb-section-lbl">Recent Runs</div>
        {runsLoading ? (
          <>
            <div className="cc-sb-runs-skel-row" />
            <div className="cc-sb-runs-skel-row" />
            <div className="cc-sb-runs-skel-row" />
          </>
        ) : recentRuns.length === 0 ? (
          <div className="cc-sb-runs-empty">No routine runs yet</div>
        ) : (
          <div className="cc-sb-runs">
            {recentRuns.map((run) => {
              const cfg = RUN_STATUS_CFG[run.status] ?? RUN_STATUS_CFG.done;
              const Icon = cfg.Icon;
              return (
                <div
                  key={run.id}
                  className="cc-sb-run-item"
                  onClick={() => navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(run.routineId)}`)}
                >
                  <div className="cc-sb-run-item__icon" style={{ background: cfg.bg, color: cfg.color }}>
                    <Icon size={13} />
                  </div>
                  <div className="cc-sb-run-item__body">
                    <span className="cc-sb-run-item__title">{run.routineName}</span>
                    <span className="cc-sb-run-item__meta">
                      <FiClock size={9} style={{ verticalAlign: -1, marginRight: 3 }} />
                      {formatRunTime(run.startedAt)}{formatRunDuration(run.durationSec)}
                    </span>
                  </div>
                  <span className="cc-sb-run-item__status" style={{ background: cfg.bg, color: cfg.color }}>
                    {cfg.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutinesSidebarContent;
